import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  WORD_RECORD_LIMITS,
  WORD_STATUSES,
  type WordRecordView,
  type WordStatus,
} from '@en3/shared';
import { WORDBOOK_RELATED_KEYS } from '@/api/client';
import { toDisplayMessage } from '@/api/errors';
import { wordbookApi } from '@/api/wordbook.api';
import { Button } from '@/components/ui/button';
import { Select, Textarea } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { WORD_STATUS_LABELS } from './WordbookFilters';

/**
 * 编辑一条生词记录的笔记 / 标签 / 状态。
 *
 * 两个取舍：
 * 1. **只改这三样，不在这里改熟悉度**。熟悉度是间隔重复的输入，手拖动它会让
 *    "下次什么时候复习"变得不可解释；想调整记忆强度应该通过复习时的自评（见复习页）。
 * 2. **标签用一行文本输入而不是标签编辑器**：8 个以内的标签，逗号分隔的输入法
 *    在移动端最快，也避免再写一个"打标签"组件。分隔符同时接受中英文逗号与顿号/空格，
 *    因为中文输入法下用户都会顺手打「，」。
 *
 * 组件不做"打开/关闭"的自我管理：父组件在 `record` 非空时才挂载它，
 * 这样每次打开都是全新挂载，不会残留上一次编辑的内容（比用 effect 手动重置更可靠）。
 *
/**
 * 标签解析：把一行文本变成标签数组。
 *
 * 分隔符同时接受中英文逗号、顿号与空格 —— 中文输入法下用户都会顺手打「，」，
 * 只认半角逗号会让人以为"打了没生效"。只做去空与去重；
 * **超长标签不在这里丢掉**：留给下面用 `longTag` 明确报错，
 * 否则用户打了 30 个字却只看到"标签消失"，不知道发生了什么。
 */
function parseTags(raw: string): string[] {
  const parts = raw
    .split(/[,，、\s]+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return Array.from(new Set(parts));
}

export function WordNoteDialog({
  record,
  onClose,
  onSaved,
}: {
  record: WordRecordView;
  onClose: () => void;
  /** 保存成功后调用（父组件据此显示一条 role="status" 提示并关闭） */
  onSaved?: () => void;
}) {
  const queryClient = useQueryClient();
  const noteId = useId();
  const tagsId = useId();
  const statusId = useId();

  const [note, setNote] = useState(record.note ?? '');
  const [tagsText, setTagsText] = useState(record.tags.join('、'));
  const [status, setStatus] = useState<WordStatus>(record.status);

  const tags = parseTags(tagsText);
  // 前端先按同一份常量校验一遍：后端用的是 shared 里的同名 schema，两边口径完全一致，
  // 这样用户不会为了一个"标签超过 8 个"白等一次往返。
  const tooManyTags = tags.length > WORD_RECORD_LIMITS.maxTags;
  const longTag = tags.find((tag) => tag.length > WORD_RECORD_LIMITS.maxTagChars);
  const noteTooLong = note.trim().length > WORD_RECORD_LIMITS.maxNoteChars;
  const invalid = tooManyTags || longTag !== undefined || noteTooLong;

  const save = useMutation({
    mutationFn: () =>
      wordbookApi.update(record.id, {
        note: note.trim().length > 0 ? note.trim() : null,
        tags,
        status,
      }),
    onSuccess: async () => {
      // 生词本是多个页面的数据源：失效 ['wordbook']/['me']/['review']（见 WORDBOOK_RELATED_KEYS）
      await Promise.all(WORDBOOK_RELATED_KEYS.map((key) => queryClient.invalidateQueries({ queryKey: key })));
      onSaved?.();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      variant="sheet"
      title={`编辑「${record.surface}」`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            取消
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={invalid}>
            保存
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
          <p className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{record.surface}</span>
            {(record.phoneticUk ?? record.phoneticUs) && (
              <span className="font-mono text-xs text-muted-foreground">{record.phoneticUk ?? record.phoneticUs}</span>
            )}
            {record.pos && <span className="text-xs text-muted-foreground">{record.pos}</span>}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">{record.translation ?? '暂无释义'}</p>
        </div>

        <Field
          id={noteId}
          label="我的笔记"
          hint={`最多 ${WORD_RECORD_LIMITS.maxNoteChars} 字。复习翻面时会显示这句话。`}
          error={noteTooLong ? `笔记最多 ${WORD_RECORD_LIMITS.maxNoteChars} 字` : undefined}
        >
          <Textarea
            id={noteId}
            rows={4}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="例如：易混词 adapt / adopt；记住 ad- 表方向"
            aria-invalid={noteTooLong || undefined}
          />
        </Field>

        <Field
          id={tagsId}
          label="标签"
          hint={`逗号分隔，最多 ${WORD_RECORD_LIMITS.maxTags} 个`}
          error={
            tooManyTags
              ? `标签最多 ${WORD_RECORD_LIMITS.maxTags} 个`
              : longTag
                ? `单个标签最多 ${WORD_RECORD_LIMITS.maxTagChars} 个字符：「${longTag}」`
                : undefined
          }
        >
          <Input
            id={tagsId}
            value={tagsText}
            onChange={(event) => setTagsText(event.target.value)}
            placeholder="期末重点、Unit 3"
            autoComplete="off"
            aria-invalid={tooManyTags || longTag !== undefined || undefined}
          />
        </Field>

        <Field id={statusId} label="状态" hint="状态影响筛选与首页统计，不直接改变复习间隔。">
          <Select id={statusId} value={status} onChange={(event) => setStatus(event.target.value as WordStatus)}>
            {WORD_STATUSES.map((item) => (
              <option key={item} value={item}>
                {WORD_STATUS_LABELS[item]}
              </option>
            ))}
          </Select>
        </Field>

        {save.isError && (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            保存失败：{toDisplayMessage(save.error)}
          </p>
        )}
      </div>
    </Dialog>
  );
}
